import { FILL_DEFAULTS, resolveFillSettings, isFillPass } from "../SvgEditor/helpers/fillSettings";
import AutoSatinBeta from './AutoSatinBeta';
import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Select, Tooltip, Slider, Switch, Modal, InputNumber, Popover } from "antd";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { HexColorPicker, HexColorInput } from "react-colorful";
import UndoIcon from "../../assets/SvgEditor/Undo.svg";
import RedoIcon from "../../assets/SvgEditor/Redo.svg";
import { SATIN_PULL_COMP_MM } from "../SvgEditor/helpers/injectEmbroideryAttributes";
import { getSatinColumnColor } from "./satinPalette";
import SatinColumnControls from "./SatinColumnControls";

// Embroidery-flavoured icon set — replaces the inconsistent emoji glyphs.
// All draw with currentColor so they inherit each button's existing colour.
const FillTypeIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden style={{ display: "block" }}>
    <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="2.4" stroke="currentColor" strokeWidth="1.3" />
    <path d="M4.6 6h6.8M4.6 8h6.8M4.6 10h6.8" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const SatinTypeIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M5 2.6v10.8M11 2.6v10.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M5 4.7l6 2.1M5 7.4l6 2.1M5 10.1l6 2.1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const NeedleIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M4.5 19.5L17 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <circle cx="17.6" cy="6.4" r="1.15" stroke="currentColor" strokeWidth="1.3" />
    <path d="M17.6 6.4c1.1-1.6 3.4-1.3 3.4.5 0 1.3-1.5 1.8-2.6 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
  </svg>
);

const RunningTypeIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M2.4 8h2.6M6.7 8h2.6M11 8h2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Split: a shape divided into two pieces by a dashed cut line.
const SplitIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <rect x="3" y="5" width="7" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <rect x="14" y="5" width="7" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 3.5v17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2.4 2.4" />
  </svg>
);

// Cut: scissors with a small x to signal "cut away / remove".
const CutScissorsIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <circle cx="5" cy="7.5" r="2.3" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="5" cy="16.5" r="2.3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7 9L16.5 14M7 15L16.5 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M18.5 4.5l3.5 3.5M22 4.5l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const BucketIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M10.4 3.1l1.4 1.4M5 11l6.2-6.2 6.5 6.5a1.3 1.3 0 0 1 0 1.9l-5 5a1.3 1.3 0 0 1-1.9 0L5 12.9a1.3 1.3 0 0 1 0-1.9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    <path d="M20 14.6s1.5 1.9 1.5 3a1.5 1.5 0 1 1-3 0c0-1.1 1.5-3 1.5-3z" fill="currentColor" />
  </svg>
);

const EraserIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M8.6 19.6h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M14.7 5.3l4 4a1.55 1.55 0 0 1 0 2.2l-6 6H8.5l-3.2-3.2a1.55 1.55 0 0 1 0-2.2l7.2-6.8a1.55 1.55 0 0 1 2.2 0z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const PencilIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M4 20h4l10.5-10.5a1.8 1.8 0 0 0 0-2.5l-1.5-1.5a1.8 1.8 0 0 0-2.5 0L4 16z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// Offset: a shape outline with arrows pointing outward — expand/shrink.
const MorphIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 4.5V2M12 4.5l-1.6-1.6M12 4.5l1.6-1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 19.5V22M12 19.5l-1.6 1.6M12 19.5l1.6 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.5 12H2M4.5 12l-1.6-1.6M4.5 12l-1.6 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.5 12H22M19.5 12l1.6-1.6M19.5 12l1.6 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyedropperIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block" }}>
    <path d="M17.6 2.6a2.45 2.45 0 0 1 3.8 3.8l-1.7 1.7-3.8-3.8 1.7-1.7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M15.4 5.1L6 14.5c-.3.3-.5.7-.6 1.1l-.8 3.3 3.3-.8c.4-.1.8-.3 1.1-.6l9.4-9.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const STITCH_TYPES = [
  { value: "auto_fill", label: "Fill", Icon: FillTypeIcon, hint: "Fill — solid stitching for large filled areas" },
  { value: "satin", label: "Satin", Icon: SatinTypeIcon, hint: "Satin — smooth, glossy borders and lettering" },
  { value: "running", label: "Running", Icon: RunningTypeIcon, hint: "Running — thin single-line outlines and fine detail" },
];

const FILL_METHODS = [
  { value: "auto_fill", label: "Auto Fill" },
  { value: "contour_fill", label: "Contour" },
  { value: "circular_fill", label: "Circular" },
  { value: "meander_fill", label: "Meander" },
];

const ANGLE_PRESETS = [
  { value: null, label: "Auto" },
  { value: 0, label: "0°" },
  { value: 45, label: "45°" },
  { value: 90, label: "90°" },
  { value: 135, label: "135°" },
];

const UNDERLAY_TYPES = [
  { value: "auto", label: "Auto" },
  { value: "zigzag", label: "Zigzag" },
  { value: "edge", label: "Edge" },
  { value: "centerwalk", label: "Centerwalk" },
];

const TOOLTIP_STYLE = {
  fontFamily: "Clash Grotesk",
  fontWeight: 500,
  fontSize: "11px",
  padding: "3px 8px",
  borderRadius: "6px",
};

// Render a labeled row with a slider + numeric input + units. Used inside the
// "More" popover for every numeric autofill setting.
const SliderRow = ({ label, hint, value, onChange, min, max, step, unit, decimals = 2 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "4px 0" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
      <span style={{ fontSize: "11px", fontWeight: 600, color: "#333" }}>
        {label}
        {hint && (
          <Tooltip title={hint} placement="top" color="#333" overlayInnerStyle={TOOLTIP_STYLE}>
            <span style={{ marginLeft: "5px", fontSize: "10px", color: "#bbb", cursor: "help" }}>ⓘ</span>
          </Tooltip>
        )}
      </span>
      <InputNumber
        size="small"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(v) => v != null && onChange(v)}
        controls={false}
        style={{ width: "88px", fontFamily: "Clash Grotesk, sans-serif" }}
        addonAfter={unit ? <span style={{ fontSize: "10px" }}>{unit}</span> : null}
        formatter={(v) => (typeof v === "number" ? v.toFixed(decimals) : v)}
      />
    </div>
    <Slider
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      tooltip={{ formatter: (v) => `${(v ?? 0).toFixed(decimals)}${unit ? ` ${unit}` : ""}` }}
      styles={{ track: { background: "#a855f7" }, handle: { borderColor: "#a855f7" } }}
    />
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: "10px", fontWeight: 700, color: "#6b7280",
    letterSpacing: "0.6px", textTransform: "uppercase",
    marginTop: "4px", marginBottom: "6px",
    paddingBottom: "5px",
    borderBottom: "1px solid #f0f0f0",
  }}>{children}</div>
);

const ToggleSettingRow = ({ label, hint, checked, onChange }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
    <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>
      {label}
      {hint && (
        <Tooltip title={hint} placement="top" color="#333" overlayInnerStyle={TOOLTIP_STYLE}>
          <span style={{ marginLeft: "5px", fontSize: "10px", color: "#bbb", cursor: "help" }}>ⓘ</span>
        </Tooltip>
      )}
    </span>
    <Switch size="small" checked={checked} onChange={onChange} style={{ background: checked ? "#7C3AED" : undefined }} />
  </div>
);

const RUNNING_DEFAULTS = {
  running_stitch_length_mm: 2.5,
  bean_stitch_repeats: 0,
  randomize_stitch_length: false,
};

const SATIN_DEFAULTS = {
  pull_compensation_mm: SATIN_PULL_COMP_MM,
  push_compensation_mm: 0,
  zigzag_spacing_mm: 0.4,
  max_stitch_length_mm: 7.0,
  split_method: "staggered",
  split_staggers: 4,
  short_stitch_distance_mm: 0.25,
  short_stitch_inset: 15,
  fill_underlay: true,
  satin_underlay_profile: "auto",
  contour_underlay_inset_mm: 0.4,
  zigzag_underlay_spacing_mm: 3.0,
  reverse_rails: "automatic",
  swap_satin_rails: false,
};

// Specialist satin controls stay behind Advanced settings; pull compensation
// and zigzag spacing remain directly accessible.
const SatinAdvancedSettings = ({
  stitchSettings,
  setStitchSettings,
  embroidery,
  currentLayerIdx,
  activePassIndex,
}) => {
  const updateField = (patch) => {
    setStitchSettings((prev) => ({ ...prev, ...patch }));
    embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, patch);
  };

  return (
    <div style={{ fontFamily: "Clash Grotesk, sans-serif" }}>
      <SectionTitle>Compensation</SectionTitle>
      <SliderRow
        label="Push compensation"
        hint="Shortens the start and end of the column to counter satin stitches pushing beyond the drawn endpoints."
        value={stitchSettings.push_compensation_mm ?? 0}
        onChange={(v) => updateField({ push_compensation_mm: v })}
        min={0} max={1.0} step={0.05} unit="mm"
      />
      <SectionTitle>Long &amp; Short Stitches</SectionTitle>
      <SliderRow
        label="Maximum satin stitch"
        hint="Stitches wider than this are split so they are less likely to snag. Most home machines are happiest below about 7–10 mm."
        value={stitchSettings.max_stitch_length_mm ?? 7.0}
        onChange={(v) => updateField({ max_stitch_length_mm: v })}
        min={3} max={12} step={0.5} unit="mm" decimals={1}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Split method</span>
        <Select
          size="small"
          value={stitchSettings.split_method || "staggered"}
          onChange={(v) => updateField({ split_method: v })}
          style={{ width: "140px", fontFamily: "Clash Grotesk, sans-serif" }}
          options={[
            { value: "default", label: "Automatic" },
            { value: "simple", label: "Aligned" },
            { value: "staggered", label: "Staggered" },
          ]}
        />
      </div>
      {(stitchSettings.split_method || "staggered") === "staggered" && (
        <SliderRow
          label="Stagger cycle"
          hint="Offsets neighboring split points so they do not form a visible valley in the satin."
          value={stitchSettings.split_staggers ?? 4}
          onChange={(v) => updateField({ split_staggers: v })}
          min={2} max={8} step={0.5} unit="rows" decimals={1}
        />
      )}
      <SliderRow
        label="Short-stitch threshold"
        hint="On tight curves, stitches closer than this are inset to avoid too many needle penetrations at the inside edge."
        value={stitchSettings.short_stitch_distance_mm ?? 0.25}
        onChange={(v) => updateField({ short_stitch_distance_mm: v })}
        min={0} max={1.5} step={0.05} unit="mm"
      />
      <SliderRow
        label="Short-stitch inset"
        hint="How far short stitches pull away from the inside edge. Increase it for sharp bends; use 0% to disable."
        value={stitchSettings.short_stitch_inset ?? 15}
        onChange={(v) => updateField({ short_stitch_inset: v })}
        min={0} max={50} step={1} unit="%" decimals={0}
      />

      <SectionTitle>Underlay</SectionTitle>
      <ToggleSettingRow
        label="Enable underlay"
        hint="Stabilizes the fabric before the top satin is sewn."
        checked={stitchSettings.fill_underlay !== false}
        onChange={(v) => updateField({ fill_underlay: v })}
      />
      {stitchSettings.fill_underlay !== false && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Underlay profile</span>
            <Select
              size="small"
              value={stitchSettings.satin_underlay_profile || "auto"}
              onChange={(v) => updateField({ satin_underlay_profile: v })}
              style={{ width: "160px", fontFamily: "Clash Grotesk, sans-serif" }}
              options={[
                { value: "auto", label: "Auto by width" },
                { value: "center", label: "Center walk" },
                { value: "center_edge", label: "Center + edge" },
                { value: "full", label: "Center + edge + zigzag" },
              ]}
            />
          </div>
          {(stitchSettings.satin_underlay_profile || "auto") === "auto" && (
            <p style={{ fontSize: 11, color: "#6b7280", margin: "4px 0 8px" }}>
              Up to 2 mm: center walk. Over 2 mm: add edge underlay.
              Over 4 mm: also add zigzag. Your stitch spacing and edge inset stay as set.
            </p>
          )}
          <SliderRow
            label="Edge inset"
            hint="Fixed distance inside the satin rails for contour underlay. Export uses this value without adding pull compensation or a percentage."
            value={stitchSettings.contour_underlay_inset_mm ?? 0.4}
            onChange={(v) => updateField({ contour_underlay_inset_mm: v })}
            min={0.1} max={1.5} step={0.05} unit="mm"
          />
          {stitchSettings.satin_underlay_profile === "full" && (
            <SliderRow
              label="Zigzag underlay spacing"
              hint="Spacing of the low-density zigzag foundation used for wide columns and difficult fabrics."
              value={stitchSettings.zigzag_underlay_spacing_mm ?? 3.0}
              onChange={(v) => updateField({ zigzag_underlay_spacing_mm: v })}
              min={1} max={6} step={0.25} unit="mm"
            />
          )}
        </>
      )}

      <SectionTitle>Rail Routing</SectionTitle>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Rail direction</span>
        <Select
          size="small"
          value={stitchSettings.reverse_rails || "automatic"}
          onChange={(v) => updateField({ reverse_rails: v })}
          style={{ width: "160px", fontFamily: "Clash Grotesk, sans-serif" }}
          options={[
            { value: "automatic", label: "Auto-correct" },
            { value: "none", label: "Keep as drawn" },
            { value: "first", label: "Reverse edge 1" },
            { value: "second", label: "Reverse edge 2" },
            { value: "both", label: "Reverse both" },
          ]}
        />
      </div>
      <ToggleSettingRow
        label="Swap edges"
        hint="Swaps edge 1 and edge 2, changing which side the thread finishes on and how sided settings apply."
        checked={!!stitchSettings.swap_satin_rails}
        onChange={(v) => updateField({ swap_satin_rails: v })}
      />
    </div>
  );
};

// Body of the "More" settings dialog — fill or running stitch params.
// Laid out as two equal columns to make proper use of the modal width.
const AutoFillAdvancedSettings = ({
  stitchSettings,
  setStitchSettings,
  embroidery,
  currentLayerIdx,
  activePassIndex,
  isRunning,
  onReset,
  singleColumn = false,
}) => {
  const updateField = (patch) => {
    setStitchSettings((prev) => ({ ...prev, ...patch }));
    embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, patch);
  };

  const col = { display: "flex", flexDirection: "column", gap: "0px", flex: 1, minWidth: 0 };
  const divider = singleColumn
    ? { height: "1px", background: "#f0f0f0", flexShrink: 0, margin: "10px 0" }
    : { width: "1px", background: "#f0f0f0", flexShrink: 0, alignSelf: "stretch", margin: "0 20px" };

  if (isRunning) {
    return (
      <div style={{ fontFamily: "Clash Grotesk, sans-serif" }}>
        <SectionTitle>Running Stitch</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: singleColumn ? "1fr" : "1fr 1fr", gap: "0 32px" }}>
          <SliderRow
            label="Stitch length"
            hint="How long each stitch is along the line. Shorter follows tight curves better; longer sews faster."
            value={stitchSettings.running_stitch_length_mm ?? 2.5}
            onChange={(v) => updateField({ running_stitch_length_mm: v })}
            min={1.0} max={5.0} step={0.1} unit="mm" decimals={1}
          />
          <SliderRow
            label="Bean repeats"
            hint="0 = a single thin line. Higher numbers retrace the line for a thicker, bolder outline (bean stitch)."
            value={stitchSettings.bean_stitch_repeats ?? 0}
            onChange={(v) => updateField({ bean_stitch_repeats: Math.round(v) })}
            min={0} max={5} step={1} unit="" decimals={0}
          />
        </div>
        <ToggleSettingRow
          label="Randomize length"
          hint="Slightly varies stitch lengths so the line looks hand-sewn instead of mechanical."
          checked={!!stitchSettings.randomize_stitch_length}
          onChange={(v) => updateField({ randomize_stitch_length: v })}
        />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Clash Grotesk, sans-serif" }}>
      <div style={{ display: "flex", flexDirection: singleColumn ? "column" : "row", gap: "0", alignItems: singleColumn ? "stretch" : "flex-start" }}>
        {/* Left column: Fill pattern */}
        <div style={col}>
          <SectionTitle>Density</SectionTitle>
          <SliderRow
            label="Row spacing"
            hint="Space between each row of stitches. Smaller numbers = denser, fuller fill (and more thread)."
            value={stitchSettings.row_spacing_mm ?? 0.35}
            onChange={(v) => updateField({ row_spacing_mm: v })}
            min={0.1} max={1.0} step={0.05} unit="mm"
          />

          <SectionTitle>Stitches</SectionTitle>
          <SliderRow
            label="Max stitch length"
            hint="The longest a single stitch can be. Long stitches snag easily, so they're split into smaller ones."
            value={stitchSettings.max_stitch_length_mm ?? 3.0}
            onChange={(v) => updateField({ max_stitch_length_mm: v })}
            min={1.0} max={6.0} step={0.1} unit="mm" decimals={1}
          />
          <SliderRow
            label="Stagger lines"
            hint="Shifts where stitches start on each row so they don't line up into a visible ridge. Higher = more scattered."
            value={stitchSettings.staggers ?? 4}
            onChange={(v) => updateField({ staggers: Math.round(v) })}
            min={1} max={8} step={1} unit="" decimals={0}
          />
          <ToggleSettingRow
            label="Randomize length"
            hint="Slightly varies stitch lengths so they don't form a visible grid. Gives a more natural look."
            checked={!!stitchSettings.randomize_stitch_length}
            onChange={(v) => updateField({ randomize_stitch_length: v })}
          />
        </div>

        <div style={divider} />

        {/* Right column: Compensation + Underlay */}
        <div style={col}>
          <SectionTitle>Compensation</SectionTitle>
          <SliderRow
            label="Pull compensation"
            hint="Stitching pulls fabric inward and narrows shapes. This adds a little width back so they end up the right size."
            value={stitchSettings.pull_compensation_mm ?? 0.2}
            onChange={(v) => updateField({ pull_compensation_mm: v })}
            min={0} max={1.0} step={0.05} unit="mm"
          />
          <SliderRow
            label="Expand"
            hint="Grows (or with negative values, shrinks) the filled area so it slightly overlaps neighbours and avoids gaps."
            value={stitchSettings.expand_mm ?? 0}
            onChange={(v) => updateField({ expand_mm: v })}
            min={-1.0} max={2.0} step={0.1} unit="mm" decimals={1}
          />

          <SectionTitle>Underlay</SectionTitle>
          <ToggleSettingRow
            label="Enable underlay"
            hint="A light layer of stitches underneath that stops the fabric puckering and makes the top fill look smoother."
            checked={stitchSettings.fill_underlay !== false}
            onChange={(v) => updateField({ fill_underlay: v })}
          />
          {stitchSettings.fill_underlay !== false && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Type</span>
                <Select
                  size="small"
                  value={stitchSettings.underlay_type || "auto"}
                  onChange={(v) => updateField({ underlay_type: v })}
                  style={{ width: "140px", fontFamily: "Clash Grotesk, sans-serif" }}
                  options={UNDERLAY_TYPES}
                />
              </div>
              <SliderRow
                label="Underlay spacing"
                hint="Space between the underlay rows. It's normally wider than the top fill since it's just a base."
                value={stitchSettings.fill_underlay_row_spacing_mm ?? 3.0}
                onChange={(v) => updateField({ fill_underlay_row_spacing_mm: v })}
                min={1.0} max={6.0} step={0.1} unit="mm" decimals={1}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Underlay angle</span>
                <div style={{ display: "flex", gap: "1px" }}>
                  {ANGLE_PRESETS.map((a) => {
                    const isActive = stitchSettings.fill_underlay_angle === a.value;
                    return (
                      <button
                        key={`u-${a.label}`}
                        onClick={() => updateField({ fill_underlay_angle: a.value })}
                        style={{
                          padding: "3px 7px", fontSize: "10px", fontWeight: 600,
                          border: "none", borderRadius: "4px", cursor: "pointer",
                          background: isActive ? "#f3e8ff" : "transparent",
                          color: isActive ? "#7C3AED" : "#888",
                          fontFamily: "Clash Grotesk, sans-serif",
                        }}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Sep = () => (
  <div style={{ width: "1px", height: "18px", background: "#e0e0e0", margin: "0 1px", flexShrink: 0 }} />
);

// Slim left-edge tab shown on mobile when a docked panel is collapsed, so the
// whole canvas stays visible. Tapping it re-opens the panel.
const EdgeHandle = ({ label, accent, swatch, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Open ${label} panel`}
    style={{
      position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
      zIndex: 121, display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
      background: "rgba(255,255,255,0.98)",
      border: "1px solid rgba(0,0,0,0.08)", borderLeft: "none",
      borderRadius: "0 13px 13px 0",
      boxShadow: "0 6px 20px rgba(0,0,0,0.16)",
      backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
      padding: "13px 8px", cursor: "pointer", pointerEvents: "auto",
      fontFamily: "Clash Grotesk, sans-serif",
    }}
  >
    {swatch && (
      <span style={{ width: "13px", height: "13px", borderRadius: "4px", backgroundColor: swatch, border: "1px solid rgba(0,0,0,0.12)" }} />
    )}
    <RightOutlined style={{ fontSize: "11px", color: accent }} />
    <span style={{ writingMode: "vertical-rl", fontSize: "11px", fontWeight: 700, color: accent, letterSpacing: "0.04em" }}>
      {label}
    </span>
  </button>
);

// Small minimize control placed in a mobile panel header to collapse it.
const CollapseButton = ({ onClick }) => (
  <button
    onClick={onClick}
    aria-label="Collapse panel"
    title="Hide panel"
    style={{
      background: "none", border: "none", cursor: "pointer", color: "#9ca3af",
      fontSize: "13px", padding: "4px", borderRadius: "6px", display: "flex", alignItems: "center",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "#111"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
  >
    <LeftOutlined />
  </button>
);

// Small group eyebrow used in the contextual secondary bar (e.g. "Angle").
const BarLabel = ({ children }) => (
  <span style={{
    fontSize: "10px", fontWeight: 700, color: "#9ca3af",
    textTransform: "uppercase", letterSpacing: "0.04em",
    whiteSpace: "nowrap", padding: "0 4px 0 2px",
  }}>
    {children}
  </span>
);

// Full-width illustrated card-button for the three Satin column methods.
// Clicking it starts the tool immediately; the matching walkthrough then
// appears on the active screen as a visual reference.
const SatinMethodButton = ({ accent, bg, border, title, desc, icon, onClick, compact = false }) => (
  <button
    type="button"
    title={compact ? desc : undefined}
    data-tutorial={`satin-method-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", gap: "11px", textAlign: "left",
      width: "100%", padding: compact ? "6px 10px" : "8px 10px", borderRadius: "11px",
      background: "#fff", border: `1px solid ${border}`, cursor: "pointer",
      fontFamily: "Clash Grotesk, sans-serif", transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = bg; e.currentTarget.style.boxShadow = `0 2px 10px ${bg}`; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "none"; }}
  >
    <span style={{
      flexShrink: 0, width: compact ? "32px" : "58px", height: compact ? "28px" : "42px", borderRadius: "9px",
      background: bg, border: `1px solid ${border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <span style={{ display: "flex", transform: compact ? "scale(0.7)" : undefined }}>{icon}</span>
    </span>
    <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.32, minWidth: 0 }}>
      <span style={{ fontSize: "12px", fontWeight: 700, color: accent }}>{title}</span>
      {!compact && <span style={{ fontSize: "10px", color: "#9ca3af" }}>{desc}</span>}
    </span>
  </button>
);

const SATIN_METHOD_ICONS = {
  split: (
    <svg width="44" height="30" viewBox="0 0 44 30" fill="none" aria-hidden>
      <ellipse cx="22" cy="15" rx="15" ry="10" stroke="#e8d3ad" strokeWidth="1.4" />
      <path d="M11 15h22" stroke="#d97706" strokeWidth="1.2" strokeDasharray="2.5 2.5" />
      <circle cx="8" cy="15" r="2.6" fill="#d97706" />
      <circle cx="36" cy="15" r="2.6" fill="#d97706" />
    </svg>
  ),
  select: (
    <svg width="44" height="30" viewBox="0 0 44 30" fill="none" aria-hidden>
      {/* Satin band filling the gap between an outer and inner path (an "O") */}
      <path
        d="M22 4v6.5M22 19.5V26M6.5 15h9M28.5 15H37.5M11 7.5l4 3M33 7.5l-4 3M11 22.5l4-3M33 22.5l-4-3"
        stroke="#c4b5fd" strokeWidth="1.2" strokeLinecap="round"
      />
      <ellipse cx="22" cy="15" rx="15.5" ry="11" stroke="#7C3AED" strokeWidth="2.2" />
      <ellipse cx="22" cy="15" rx="6.5" ry="4.5" stroke="#7C3AED" strokeWidth="2" />
    </svg>
  ),
  draw: (
    <svg width="44" height="30" viewBox="0 0 44 30" fill="none" aria-hidden>
      <path d="M12 6c4 6 4 13 0 19" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M30 7c4 6 4 12 0 18" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3" />
      <path d="M33 4l5 5-2.4 2.4-5-5L33 4z" fill="#3b82f6" />
      <path d="M30.6 6.4l5 5-6.1 6.1-3.2.8.8-3.2 6.1-6.1z" stroke="#3b82f6" strokeWidth="1.3" strokeLinejoin="round" fill="none" />
    </svg>
  ),
  border: (
    <svg width="44" height="30" viewBox="0 0 44 30" fill="none" aria-hidden>
      {/* A satin band wrapping the outline: outer + inner rail with rungs. */}
      <rect x="6" y="5" width="32" height="20" rx="6" stroke="#0d9488" strokeWidth="2.4" />
      <rect x="11" y="9.5" width="22" height="11" rx="3.5" stroke="#5eead4" strokeWidth="1.6" />
      <path d="M22 5v4.5M22 20.5V25M6 15h5M33 15h5" stroke="#0d9488" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
};

// ── Satin method tutorials ────────────────────────────────────────────────
// Each satin column method opens an illustrated, step-by-step walkthrough so
// users understand the interaction *before* they start clicking on the canvas.

// A little mouse-cursor glyph used inside the step illustrations.
const TutCursor = ({ x, y, color = "#1f2937" }) => (
  <g transform={`translate(${x} ${y})`}>
    <path
      d="M0 0L0 17L4.4 12.8L7.6 19.6L10.2 18.4L7 11.8L13 11.8Z"
      fill="#fff" stroke={color} strokeWidth="1.4" strokeLinejoin="round"
    />
  </g>
);

// A small pen-nib glyph for the "Draw Edges" walkthrough.
const TutPen = ({ x, y, color = "#3b82f6" }) => (
  <g transform={`translate(${x} ${y})`}>
    <path d="M0 19L2.4 12.5L11 4L16 9L7.5 17.6Z" fill="#fff" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M11 4L14 1L19 6L16 9Z" fill={color} />
    <path d="M0 19L1.4 15.5L4.5 17.4Z" fill={color} />
  </g>
);

// Shared frame so every illustration sits on the same soft card.
const TutFrame = ({ bg, children }) => (
  <svg viewBox="0 0 150 96" width="100%" height="auto" style={{ display: "block", borderRadius: "10px", background: bg }} aria-hidden>
    {children}
  </svg>
);

const SATIN_TUTORIALS = {
  split: {
    title: "Split into Satin Columns",
    accent: "#d97706",
    soft: "rgba(217,119,6,0.08)",
    border: "rgba(217,119,6,0.28)",
    intro: "Turn a single closed shape into a satin column by marking the two edges the stitches should run between. Best for leaves, petals and other long, symmetric shapes.",
    cta: "Start splitting",
    steps: [
      {
        label: "Click one edge of the shape's outline to drop the first marker.",
        art: (
          <TutFrame bg="rgba(217,119,6,0.06)">
            <ellipse cx="75" cy="48" rx="50" ry="29" fill="#fff7ec" stroke="#e3c79a" strokeWidth="2" />
            <circle cx="25" cy="48" r="9.5" fill="none" stroke="#d97706" strokeWidth="1.4" opacity="0.45" />
            <circle cx="25" cy="48" r="5.5" fill="#d97706" />
            <TutCursor x={30} y={52} color="#d97706" />
          </TutFrame>
        ),
      },
      {
        label: "Click the opposite edge. The two markers become the left and right rails.",
        art: (
          <TutFrame bg="rgba(217,119,6,0.06)">
            <ellipse cx="75" cy="48" rx="50" ry="29" fill="#fff7ec" stroke="#e3c79a" strokeWidth="2" />
            <line x1="31" y1="48" x2="119" y2="48" stroke="#d97706" strokeWidth="1.6" strokeDasharray="4 4" />
            <circle cx="25" cy="48" r="5.5" fill="#d97706" />
            <circle cx="125" cy="48" r="9.5" fill="none" stroke="#d97706" strokeWidth="1.4" opacity="0.45" />
            <circle cx="125" cy="48" r="5.5" fill="#d97706" />
            <TutCursor x={130} y={52} color="#d97706" />
          </TutFrame>
        ),
      },
      {
        label: "Satin stitches fill back and forth between the two rails.",
        art: (
          <TutFrame bg="rgba(217,119,6,0.06)">
            <defs>
              <clipPath id="tutSplitClip"><ellipse cx="75" cy="48" rx="50" ry="29" /></clipPath>
            </defs>
            <ellipse cx="75" cy="48" rx="50" ry="29" fill="#fff7ec" stroke="#e3c79a" strokeWidth="2" />
            <g clipPath="url(#tutSplitClip)" stroke="#d97706" strokeWidth="2" opacity="0.85">
              {Array.from({ length: 17 }).map((_, i) => (
                <line key={i} x1={28 + i * 5.6} y1="16" x2={28 + i * 5.6} y2="80" />
              ))}
            </g>
          </TutFrame>
        ),
      },
    ],
  },
  select: {
    title: "Select Satin Columns",
    accent: "#7C3AED",
    soft: "rgba(124,58,237,0.08)",
    border: "rgba(124,58,237,0.28)",
    intro: "Already have two separate lines in your design? Pick one as the left edge and one as the right — satin runs between them. Perfect for enclosed shapes like the letter \u201CO\u201D, where one path sits inside the other, plus stems, borders and lettering strokes.",
    cta: "Start selecting",
    steps: [
      {
        label: "Tap the outer path. It lights up as the first edge.",
        art: (
          <TutFrame bg="rgba(124,58,237,0.06)">
            <ellipse cx="75" cy="48" rx="46" ry="30" fill="none" stroke="#7C3AED" strokeWidth="4" />
            <ellipse cx="75" cy="48" rx="20" ry="12" fill="none" stroke="#d9cdf2" strokeWidth="4" />
            <TutCursor x={78} y={20} color="#7C3AED" />
          </TutFrame>
        ),
      },
      {
        label: "Tap the inner path to set the second edge.",
        art: (
          <TutFrame bg="rgba(124,58,237,0.06)">
            <ellipse cx="75" cy="48" rx="46" ry="30" fill="none" stroke="#7C3AED" strokeWidth="4" />
            <ellipse cx="75" cy="48" rx="20" ry="12" fill="none" stroke="#7C3AED" strokeWidth="4" />
            <TutCursor x={78} y={40} color="#7C3AED" />
          </TutFrame>
        ),
      },
      {
        label: "Satin stitches fill the band between the two paths — an \u201CO\u201D.",
        art: (
          <TutFrame bg="rgba(124,58,237,0.06)">
            <g stroke="#7C3AED" strokeWidth="2" opacity="0.8">
              {Array.from({ length: 22 }).map((_, i) => {
                const a = (i / 22) * Math.PI * 2;
                const ix = 75 + 20 * Math.cos(a);
                const iy = 48 + 12 * Math.sin(a);
                const ox = 75 + 46 * Math.cos(a);
                const oy = 48 + 30 * Math.sin(a);
                return <line key={i} x1={ix} y1={iy} x2={ox} y2={oy} />;
              })}
            </g>
            <ellipse cx="75" cy="48" rx="46" ry="30" fill="none" stroke="#7C3AED" strokeWidth="3" />
            <ellipse cx="75" cy="48" rx="20" ry="12" fill="none" stroke="#7C3AED" strokeWidth="3" />
          </TutFrame>
        ),
      },
    ],
  },
  draw: {
    title: "Draw Edges",
    accent: "#3b82f6",
    soft: "rgba(59,130,246,0.08)",
    border: "rgba(59,130,246,0.3)",
    intro: "No path to work with? Draw the two side edges yourself with the pen tool. Full control over the shape of your satin column.",
    cta: "Start drawing",
    steps: [
      {
        label: "Use the pen to draw the first edge — click to add points.",
        art: (
          <TutFrame bg="rgba(59,130,246,0.06)">
            <path d="M48 18c-8 16-8 44 0 60" stroke="#3b82f6" strokeWidth="3.5" strokeLinecap="round" fill="none" />
            <circle cx="48" cy="18" r="3.2" fill="#fff" stroke="#3b82f6" strokeWidth="1.8" />
            <circle cx="42" cy="48" r="3.2" fill="#fff" stroke="#3b82f6" strokeWidth="1.8" />
            <circle cx="48" cy="78" r="3.2" fill="#fff" stroke="#3b82f6" strokeWidth="1.8" />
            <TutPen x={50} y={62} color="#3b82f6" />
          </TutFrame>
        ),
      },
      {
        label: "Draw the second edge alongside the first.",
        art: (
          <TutFrame bg="rgba(59,130,246,0.06)">
            <path d="M48 18c-8 16-8 44 0 60" stroke="#3b82f6" strokeWidth="3.5" strokeLinecap="round" fill="none" />
            <path d="M102 18c8 16 8 44 0 60" stroke="#3b82f6" strokeWidth="3.5" strokeLinecap="round" fill="none" strokeDasharray="5 5" />
            <circle cx="102" cy="18" r="3.2" fill="#fff" stroke="#3b82f6" strokeWidth="1.8" />
            <circle cx="108" cy="48" r="3.2" fill="#fff" stroke="#3b82f6" strokeWidth="1.8" />
            <TutPen x={104} y={62} color="#3b82f6" />
          </TutFrame>
        ),
      },
      {
        label: "Satin stitches fill between your two drawn edges.",
        art: (
          <TutFrame bg="rgba(59,130,246,0.06)">
            <g stroke="#3b82f6" strokeWidth="2" opacity="0.85">
              {[22, 32, 42, 50, 58, 66, 74].map((y, i) => {
                const k = Math.abs(y - 48) / 30;
                const lx = 48 - 8 * (1 - k * k);
                const rx = 102 + 8 * (1 - k * k);
                return <line key={i} x1={lx} y1={y} x2={rx} y2={y} />;
              })}
            </g>
            <path d="M48 18c-8 16-8 44 0 60" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" fill="none" />
            <path d="M102 18c8 16 8 44 0 60" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" fill="none" />
          </TutFrame>
        ),
      },
    ],
  },
};

// Illustrated walkthrough shown inline on the active-tool screen so users have
// a visual reference for the interaction while they work. Steps only — no
// header or buttons, since the surrounding panel already owns those.
const SatinTutorialSteps = ({ method }) => {
  const tut = method ? SATIN_TUTORIALS[method] : null;
  if (!tut) return null;
  const ff = "Clash Grotesk, sans-serif";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "11px", marginTop: "2px" }}>
      <span style={{
        fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
        color: tut.accent, fontFamily: ff,
      }}>
        How it works
      </span>
      <p style={{ margin: 0, fontSize: "11px", lineHeight: 1.5, color: "#6b7280", fontFamily: ff }}>
        {tut.intro}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {tut.steps.map((step, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "11px" }}>
            <div style={{ position: "relative", flexShrink: 0, width: "98px" }}>
              <span style={{
                position: "absolute", top: "-6px", left: "-6px", zIndex: 1,
                width: "20px", height: "20px", borderRadius: "50%",
                background: tut.accent, color: "#fff", fontSize: "11px", fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: ff, boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
              }}>
                {i + 1}
              </span>
              <div style={{ border: `1px solid ${tut.border}`, borderRadius: "10px", overflow: "hidden" }}>
                {step.art}
              </div>
            </div>
            <p style={{ margin: 0, fontSize: "11px", lineHeight: 1.4, color: "#6b7280", fontFamily: ff }}>
              {step.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Angle preset chip — keeps the existing iris active colour but adds hover
// feedback so it reads as a clickable control.
const AngleChip = ({ label, isActive, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "3px 8px", fontSize: "10px", fontWeight: 600,
        border: "none", borderRadius: "5px", cursor: "pointer",
        background: isActive ? "#f3e8ff" : hovered ? "#f5f5f5" : "transparent",
        color: isActive ? "#7C3AED" : "#888",
        fontFamily: "Clash Grotesk, sans-serif",
        transition: "all 0.12s ease",
      }}
    >
      {label}
    </button>
  );
};

// CapCut-style toolbar tile: icon stacked above a short label, uniform size so
// the whole bar reads as one tidy grid. Used for both the stitch-type "mode"
// group and the shape-editing actions.
//   - color:      resting label colour (icon defaults to this)
//   - iconColor:  optional icon tint (lets tools keep a quiet colour cue while
//                 labels stay neutral and scannable)
//   - accent:     colour used when the tile is selected/active
//   - segmented:  selected tiles render as a raised white card (single-select)
const Tile = ({
  Icon, label, color = "#555", iconColor, accent = "#7C3AED",
  active = false, segmented = false, badge, tooltip, disableTooltip, onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const button = (
    <button
      data-tutorial={`stitch-tool-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: "3px",
        width: "54px", height: "44px", padding: "0 2px",
        background: active
          ? (segmented ? "#fff" : `${accent}1A`)
          : hovered ? "rgba(36,21,68,0.05)" : "transparent",
        boxShadow: active && segmented ? "0 1px 3px rgba(36,21,68,0.14)" : "none",
        border: "none", borderRadius: "9px", cursor: "pointer",
        color: active ? accent : color,
        fontFamily: "Clash Grotesk, sans-serif",
        fontSize: "10px", fontWeight: active ? 700 : 600,
        transition: "all 0.14s ease", flexShrink: 0,
      }}
    >
      <span style={{ display: "flex", lineHeight: 1, color: active ? accent : (iconColor || color) }}>
        <Icon size={18} />
      </span>
      <span style={{ lineHeight: 1, whiteSpace: "nowrap" }}>{label}</span>
      {badge}
    </button>
  );

  if (disableTooltip) return button;

  return (
    <Tooltip title={tooltip || label} placement="bottom" color="#333" overlayInnerStyle={TOOLTIP_STYLE}>
      {button}
    </Tooltip>
  );
};

const UndoRedoBtn = ({ disabled, onClick, title, imgSrc, disableTooltip }) => {
  const [hovered, setHovered] = useState(false);
  const button = (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: "30px", height: "30px",
        background: hovered && !disabled ? "#f3f3f3" : "transparent",
        border: "none", borderRadius: "6px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.3 : 1,
        transition: "all 0.15s ease",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <img src={imgSrc} alt={title} style={{ width: "15px", height: "15px" }} />
    </button>
  );

  if (disableTooltip) return button;

  return (
    <Tooltip title={title} placement="bottom" color="#333" overlayInnerStyle={TOOLTIP_STYLE}>
      {button}
    </Tooltip>
  );
};

const LAYER_SWATCH_SIZE = 34;

const ToolbarLayerStrip = ({ colors = [], layerSelected, currentLayerIdx, onLayerChange, isPhone }) => (
  <div
    className="hide-scrollbar"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      maxWidth: isPhone ? "calc(100vw - 32px)" : "360px",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      flexShrink: 1,
      padding: "0 2px",
    }}
  >
    <span
      style={{
        fontSize: "10px",
        fontWeight: 600,
        color: "#999",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      Layers
    </span>
    <button
      type="button"
      onClick={() => onLayerChange?.("all")}
      title="Show all layers"
      aria-label="Show all layers"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: `${LAYER_SWATCH_SIZE}px`,
        height: `${LAYER_SWATCH_SIZE}px`,
        borderRadius: "7px",
        border: layerSelected === "all" ? "2px solid #5282FF" : "2px solid transparent",
        backgroundColor: layerSelected === "all" ? "#eef2ff" : "#f0f0f0",
        boxShadow: layerSelected === "all" ? "0 0 0 2px rgba(82,130,255,0.12)" : "none",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
        transition: "all 0.15s ease",
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={layerSelected === "all" ? "#5282FF" : "#999"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    </button>
    {colors.map((color, index) => {
      const isSelected = layerSelected === index;
      return (
        <button
          key={`toolbar-layer-${index}`}
          type="button"
          onClick={() => onLayerChange?.(index)}
          title={`Layer ${index + 1}`}
          aria-label={`Select layer ${index + 1}`}
          style={{
            position: "relative",
            width: `${LAYER_SWATCH_SIZE}px`,
            height: `${LAYER_SWATCH_SIZE}px`,
            borderRadius: "7px",
            backgroundColor: color || "#888",
            border: isSelected ? "2px solid #5282FF" : "2px solid transparent",
            boxShadow: isSelected
              ? "0 0 0 2px rgba(82,130,255,0.12), 0 1px 2px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(255,255,255,0.2)"
              : "0 1px 2px rgba(0,0,0,0.06), inset 0 0 0 1px rgba(255,255,255,0.15)",
            cursor: "pointer",
            padding: 0,
            flexShrink: 0,
            transition: "border 0.12s ease, transform 0.1s ease, box-shadow 0.12s ease",
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: "2px",
              borderRadius: "5px",
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
              pointerEvents: "none",
            }}
          />
        </button>
      );
    })}
  </div>
);

const StitchToolbar = ({
  stitchSettings,
  setStitchSettings,
  embroidery,
  currentLayerIdx,
  aiAnalysis,
  colors,
  currentLayerIsSatin,
  autoSatinJobContext,
  autoSatinMmPerUnit,
  autoSatinJobRevision,
  autoSatinPath,
  autoSatinRevision,
  satinRetryRequest,
  onSatinRetryRequestHandled,
  onAutoSatinPreview,
  onAutoSatinApply,
  onAutoSatinDiscard,
  onApplySatinMode,
  onCreateSatinBorder,
  activeRailDrawing,
  railEditing,
  hasRails,
  railPairCount,
  onStartRailDrawing,
  onFinishRail,
  onCancelRail,
  onStartRailEditing,
  onFinishRailEditing,
  onDeleteRailNode,
  onReverseRailColumn,
  onCurveRailEdge,
  onSetRailNodeType,
  onClearRails,
  railSelection,
  onStartRailSelection,
  onCancelRailSelection,
  activeRungDrawing,
  onStartRungDrawing,
  onFinishRungDrawing,
  onClearRungs,
  currentRailPairs,
  svgJoined,
  onSplitRailsCreated,
  onUndoLastSplitPair,
  onCutFinished,
  layerPieces,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  layerSelected,
  onLayerChange,
  onAddSublayer,
  layerMeta,
  onStitchPanelOpenChange,
  onEraseStart,
  eraseActive,
  onEraseCancel,
  onFillStart,
  fillActive,
  onColorChange,
  onPixelEraseStart,
  pixelEraseActive,
  onDrawStart,
  drawActive,
  onMorphStart,
  morphActive,
  smartCutActive,
  onSmartCutStart,
  onSmartCutCancel,
  leaveCutTool,
  onOpenExport,
  aiLoading,
  targetWidth,
  targetHeight,
  setTargetWidth,
  setTargetHeight,
  lockAspectRatio,
  setLockAspectRatio,
  originalDimensions,
  placement = "top",
  isPhone = false,
}) => {
  const colorInteraction = useRef(null);
  useEffect(() => {
    const finish = () => { colorInteraction.current = null; };
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    window.addEventListener("keyup", finish);
    return () => {
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      window.removeEventListener("keyup", finish);
    };
  }, []);
  const runAfterLeaveCut = useCallback(async (fn) => {
    if (!leaveCutTool) {
      fn?.();
      return;
    }
    const ok = await leaveCutTool();
    if (ok !== false) fn?.();
  }, [leaveCutTool]);

  // Eyedropper: pick any on-screen color and recolor the whole selected layer.
  // Uses the native EyeDropper API (Chromium) and no-ops gracefully elsewhere.
  const pickLayerColor = useCallback(async () => {
    if (typeof window === "undefined" || typeof window.EyeDropper !== "function") return;
    try {
      const { sRGBHex } = await new window.EyeDropper().open();
      if (sRGBHex) onColorChange?.(currentLayerIdx, sRGBHex);
    } catch {
      // User pressed Escape / cancelled the picker — ignore.
    }
  }, [onColorChange, currentLayerIdx]);
  const eyedropperSupported =
    typeof window !== "undefined" && typeof window.EyeDropper === "function";

  const hasEditableLayer = typeof layerSelected === "number";
  const layerSettings = embroidery?.getLayerSettings?.(currentLayerIdx);
  const activePassIndex = layerSettings?.activePassIndex || 0;
  const activePass = layerSettings?.passes?.[activePassIndex] || layerSettings?.passes?.[0];
  const currentStitchType =
    activePass?.stitchType ||
    activePass?.fill_method ||
    (currentLayerIsSatin ? "satin" : stitchSettings.stitch_type || stitchSettings.fill_method) ||
    "auto_fill";
  const isFill = !["satin", "running"].includes(currentStitchType);
  const isSatin = currentStitchType === "satin";
  const isRunning = currentStitchType === "running";
  const layerColor = colors?.[currentLayerIdx] || "#888";
  const [columnSelection, setColumnSelection] = useState(null);
  const selectedColumnIdx = columnSelection?.layerIndex === currentLayerIdx &&
    columnSelection?.passIndex === activePassIndex ? columnSelection.pairIdx : null;
  const selectColumn = useCallback((pairIdx) => {
    setColumnSelection({ layerIndex: currentLayerIdx, passIndex: activePassIndex, pairIdx });
  }, [currentLayerIdx, activePassIndex]);
  // Returning from canvas guide/edge editing keeps that column in the inspector.
  const editingColumnIdx = railEditing?.pairIdx ?? activeRungDrawing?.pairIdx;
  useEffect(() => {
    if (editingColumnIdx != null) selectColumn(editingColumnIdx);
  }, [editingColumnIdx, selectColumn]);
  const activeRungColor = getSatinColumnColor(activeRungDrawing?.pairIdx || 0);

  // "More settings" modal — single instance, content adapts to current stitch type.
  const [moreOpen, setMoreOpen] = useState(false);
  // Width (mm) used when generating a satin border around a shape's outline.
  const [satinBorderWidthMm, setSatinBorderWidthMm] = useState(2);
  // Stitch-type chooser popover (the single "Stitch" tile).
  const [stitchMenuOpen, setStitchMenuOpen] = useState(false);
  // On mobile the settings/tool panels dock over the canvas. Let the user
  // collapse them to a slim edge handle so the full canvas stays visible.
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const toolPanelOpen =
    eraseActive || fillActive || pixelEraseActive || drawActive || morphActive || smartCutActive;
  // Always reveal the panel when it (re)opens or the active tool changes.
  useEffect(() => {
    setPanelCollapsed(false);
  }, [stitchMenuOpen, toolPanelOpen]);
  // Let the parent (and through it the canvas) know when the Stitch panel is
  // open, so the satin rail guides only render while the user is editing stitch.
  useEffect(() => {
    onStitchPanelOpenChange?.(stitchMenuOpen);
  }, [stitchMenuOpen, onStitchPanelOpenChange]);
  useEffect(() => {
    if (satinRetryRequest == null) return;
    setStitchMenuOpen(true);
  }, [satinRetryRequest]);
  // The stitch settings open as a left-docked side panel, portaled to the
  // editor container so it escapes the centered toolbar column.
  const rootRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);
  useEffect(() => {
    const el = rootRef.current;
    if (el) setPortalTarget(el.offsetParent || el.parentElement || null);
  }, []);
  const currentStitchFamily = isSatin ? "satin" : isRunning ? "running" : "auto_fill";
  const aiRecType = aiAnalysis?.layers?.[currentLayerIdx]?.recommended_stitch_type;
  const aiSuggestsDifferent =
    aiRecType && STITCH_TYPES.some((t) => t.value === aiRecType) && aiRecType !== currentStitchFamily;

  // Three-option dialog shown when switching type would discard satin edges.
  const [switchPrompt, setSwitchPrompt] = useState(null);

  // The current layer + any sublayers sharing its color group. Each member
  // carries its own stitch type, so the stitch window can switch between them.
  const currentMeta = Array.isArray(layerMeta) ? layerMeta[currentLayerIdx] : null;
  const groupTabs = (currentMeta?.groupId && Array.isArray(layerMeta))
    ? layerMeta
        .map((m, idx) => ({ ...m, idx }))
        .filter((m) => m.groupId === currentMeta.groupId)
        .map((m, pos) => ({ idx: m.idx, label: `Sub ${pos + 1}` }))
    : [];

  // Friendly display name for a stitch-type value.
  const typeLabel = (v) =>
    v === "satin" ? "Satin" : v === "running" ? "Running" : "Fill";

  const applyStitchType = (value) => {
    runAfterLeaveCut(() => {
      if (value === "satin") {
        if (onApplySatinMode) {
          onApplySatinMode(currentLayerIdx);
        } else {
          embroidery?.setLayerStitchType?.(currentLayerIdx, "satin");
          setStitchSettings((p) => ({ ...p, stitch_type: "satin" }));
        }
        return;
      }
      embroidery?.setLayerStitchType?.(currentLayerIdx, value);
      setStitchSettings((p) => ({ ...p, stitch_type: value, fill_method: value }));
    });
  };

  // Add a sublayer (a clone of the current layer in the same color group) and
  // give it the requested stitch type — keeps the original layer's stitching
  // (e.g. satin edges) intact while the new type lives on the sublayer.
  const addSublayerWithType = (value) => {
    const newIdx = onAddSublayer?.(currentLayerIdx);
    if (typeof newIdx === "number") {
      if (value === "satin") {
        if (onApplySatinMode) onApplySatinMode(newIdx);
        else { embroidery?.setLayerStitchType?.(newIdx, "satin"); }
      } else {
        embroidery?.setLayerStitchType?.(newIdx, value);
      }
      setStitchSettings((p) => ({ ...p, stitch_type: value, fill_method: value }));
    }
    setSwitchPrompt(null);
  };

  const chooseStitchType = (value) => {
    // Already on this type — nothing to do.
    if (value === currentStitchFamily) return;

    // Always confirm a type change: the user can keep the current stitching by
    // putting the new type on a sublayer, or replace this layer outright. Track
    // the originating type so the dialog can explain both choices.
    setSwitchPrompt({ value, from: currentStitchFamily });
  };
  const moreTitle = isRunning ? "Running Stitch Settings" : "Fill Stitch Settings";
  const moreSubtitle = `Layer ${currentLayerIdx + 1}`;

  const syncPassToSidebar = (pass) => {
    if (isFillPass(pass)) pass = resolveFillSettings(pass);
    setStitchSettings((p) => ({
      ...p,
      ...(isFillPass(pass) ? resolveFillSettings(pass) : {}),
      stitch_type: pass.stitchType || "auto_fill",
      fill_method: pass.fill_method || pass.stitchType || "auto_fill",
      angle: pass.angle !== undefined ? pass.angle : p.angle,
      row_spacing_mm: pass.row_spacing_mm ?? p.row_spacing_mm,
      zigzag_spacing_mm: pass.zigzag_spacing_mm ?? p.zigzag_spacing_mm,
      running_stitch_length_mm: pass.running_stitch_length_mm ?? p.running_stitch_length_mm,
      max_stitch_length_mm: pass.max_stitch_length_mm ?? (pass.stitchType === "satin" ? 7.0 : p.max_stitch_length_mm),
      staggers: pass.staggers ?? p.staggers,
      expand_mm: pass.expand_mm ?? p.expand_mm,
      fill_underlay: pass.fill_underlay ?? p.fill_underlay,
      pull_compensation_mm: pass.pull_compensation_mm ?? p.pull_compensation_mm,
      push_compensation_mm: pass.push_compensation_mm ?? p.push_compensation_mm,
      split_method: pass.split_method ?? p.split_method,
      split_staggers: pass.split_staggers ?? p.split_staggers,
      short_stitch_distance_mm: pass.short_stitch_distance_mm ?? p.short_stitch_distance_mm,
      short_stitch_inset: pass.short_stitch_inset ?? p.short_stitch_inset,
      satin_underlay_profile: pass.satin_underlay_profile ?? p.satin_underlay_profile,
      contour_underlay_inset_mm: pass.contour_underlay_inset_mm ?? p.contour_underlay_inset_mm,
      zigzag_underlay_spacing_mm: pass.zigzag_underlay_spacing_mm ?? p.zigzag_underlay_spacing_mm,
      reverse_rails: pass.reverse_rails ?? p.reverse_rails,
      swap_satin_rails: pass.swap_satin_rails ?? p.swap_satin_rails,
      bean_stitch_repeats: pass.bean_stitch_repeats ?? p.bean_stitch_repeats,
      randomize_stitch_length: pass.randomize_stitch_length ?? p.randomize_stitch_length,
    }));
  };

  // On phones: pin to the bottom of the canvas. AntD tooltips auto-flip when
  // they would overflow the viewport, so we don't need to thread an explicit
  // placement override into every inner button.
  const isBottom = placement === "bottom";

  // Inner pill style — on phones we want horizontal scroll instead of wrap so
  // the pill height stays predictable and doesn't gobble the canvas.
  const pillScrollStyle = isPhone
    ? {
        flexWrap: "nowrap",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        maxWidth: "calc(100vw - 16px)",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }
    : null;

  const layerBarPillStyle = {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "3px 8px",
    background: "rgba(255,255,255,0.97)",
    borderRadius: "12px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.05)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    pointerEvents: "auto",
    fontFamily: "Clash Grotesk, sans-serif",
    maxWidth: isPhone ? "calc(100vw - 16px)" : "420px",
    boxSizing: "border-box",
  };

  return (
    <div ref={rootRef} data-tutorial-chrome={isBottom ? "bottom" : "top"} style={{
      position: "absolute",
      ...(isBottom
        ? { bottom: "max(90px, calc(24px + env(safe-area-inset-bottom, 0px)))", top: "auto" }
        : { top: "12px" }),
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 100,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "6px",
      pointerEvents: "none",
      maxWidth: "100vw",
      width: isPhone ? "100%" : "auto",
      padding: isPhone ? "0 8px" : 0,
      boxSizing: "border-box",
    }}>
      {!isBottom && (
        <div style={{ ...layerBarPillStyle, order: 3 }}>
          <ToolbarLayerStrip
            colors={colors}
            layerSelected={layerSelected}
            currentLayerIdx={currentLayerIdx}
            onLayerChange={onLayerChange}
            isPhone={isPhone}
          />
        </div>
      )}

      {!hasEditableLayer && (
        <div style={{
          order: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          padding: "7px 14px",
          background: "rgba(255,255,255,0.97)",
          borderRadius: "12px",
          boxShadow: "0 2px 16px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.05)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          pointerEvents: "auto",
          fontFamily: "Clash Grotesk, sans-serif",
          fontSize: "12px",
          fontWeight: 600,
          color: "#6b7280",
          maxWidth: isPhone ? "calc(100vw - 16px)" : "none",
          whiteSpace: isPhone ? "normal" : "nowrap",
          textAlign: "center",
          boxSizing: "border-box",
        }}>
          <span style={{ opacity: 0.5 }}>↓ Pick a layer color to customize stitches</span>
          <span style={{ opacity: 0.3, margin: "0 4px" }}>·</span>
          <button
            type="button"
            onClick={onOpenExport}
            style={{
              color: "#F453C4",
              fontWeight: 700,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "inherit",
            }}
          >
            Export Stitch →
          </button>
        </div>
      )}

      {/* Main toolbar pill */}
      {hasEditableLayer && (
      <div style={{
        order: 2,
        display: "flex",
        alignItems: "center",
        gap: "2px",
        padding: "4px 6px",
        background: "rgba(255,255,255,0.97)",
        borderRadius: "12px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.05)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        pointerEvents: "auto",
        fontFamily: "Clash Grotesk, sans-serif",
        ...(pillScrollStyle || {}),
      }}>
        {/* Undo / Redo */}
        <UndoRedoBtn disabled={!canUndo} onClick={onUndo} title="Undo" imgSrc={UndoIcon} disableTooltip={isPhone} />
        <UndoRedoBtn disabled={!canRedo} onClick={onRedo} title="Redo" imgSrc={RedoIcon} disableTooltip={isPhone} />
        <Sep />

        {/* Stitch type — single tile that reveals the chooser in the secondary bar */}
        <Tile
          label="Stitch"
          Icon={NeedleIcon}
          active={stitchMenuOpen}
          tooltip="Choose the stitch type and adjust its settings"
          disableTooltip={isPhone}
          onClick={() => {
            if (stitchMenuOpen) { setStitchMenuOpen(false); return; }
            runAfterLeaveCut(() => {});
            setStitchMenuOpen(true);
          }}
          badge={aiSuggestsDifferent ? (
            <span style={{ position: "absolute", top: "6px", right: "9px", width: "6px", height: "6px", borderRadius: "50%", background: "linear-gradient(135deg,#F453C4,#7C3AED)" }} />
          ) : null}
        />

        {/* Shape-editing actions — each tool toggles its controls into the secondary bar */}
        <Sep />
        <Tile
          label="Split"
          Icon={SplitIcon}
          iconColor="#7C3AED"
          accent="#7C3AED"
          active={eraseActive}
          tooltip="Split — slice a shape into separate pieces"
          disableTooltip={isPhone}
          onClick={() => {
            if (eraseActive) { onEraseCancel?.(); return; }
            setStitchMenuOpen(false);
            onEraseStart?.(currentLayerIdx);
          }}
        />
        <Tile
          label="Erase"
          Icon={EraserIcon}
          iconColor="#64748b"
          accent="#64748b"
          active={pixelEraseActive}
          tooltip="Erase — brush away parts of a shape"
          disableTooltip={isPhone}
          onClick={() => {
            if (pixelEraseActive) { runAfterLeaveCut(() => {}); return; }
            setStitchMenuOpen(false);
            onPixelEraseStart?.(currentLayerIdx);
          }}
        />
        <Tile
          label="Draw"
          Icon={PencilIcon}
          iconColor="#16a34a"
          accent="#16a34a"
          active={drawActive}
          tooltip="Draw — brush in new areas of a shape"
          disableTooltip={isPhone}
          onClick={() => {
            if (drawActive) { runAfterLeaveCut(() => {}); return; }
            setStitchMenuOpen(false);
            onDrawStart?.(currentLayerIdx);
          }}
        />
        <Tile
          label="Fill"
          Icon={BucketIcon}
          iconColor="#ea580c"
          accent="#ea580c"
          active={fillActive}
          tooltip="Fill — fill empty gaps inside a shape"
          disableTooltip={isPhone}
          onClick={() => {
            if (fillActive) { runAfterLeaveCut(() => {}); return; }
            setStitchMenuOpen(false);
            onFillStart?.(currentLayerIdx);
          }}
        />
        <Tile
          label="Offset"
          Icon={MorphIcon}
          iconColor="#0ea5e9"
          accent="#0ea5e9"
          active={morphActive}
          tooltip="Offset — grow or shrink the shape's edges"
          disableTooltip={isPhone}
          onClick={() => {
            if (morphActive) { runAfterLeaveCut(() => {}); return; }
            setStitchMenuOpen(false);
            onMorphStart?.(currentLayerIdx);
          }}
        />
        <Tile
          label="Cut"
          Icon={CutScissorsIcon}
          iconColor="#ef4444"
          accent="#ef4444"
          active={smartCutActive}
          tooltip="Cut — remove an entire shape in one click"
          disableTooltip={isPhone}
          onClick={() => {
            if (smartCutActive) { onSmartCutCancel?.(); return; }
            setStitchMenuOpen(false);
            runAfterLeaveCut(() => onSmartCutStart?.(currentLayerIdx));
          }}
        />
      </div>
      )}

      {isBottom && (
        <div style={{ ...layerBarPillStyle, order: 1 }}>
          <ToolbarLayerStrip
            colors={colors}
            layerSelected={layerSelected}
            currentLayerIdx={currentLayerIdx}
            onLayerChange={onLayerChange}
            isPhone={isPhone}
          />
        </div>
      )}

      {/* ── Contextual tool window (Cut / Erase / Paint / Delete) — left-docked side panel ── */}
      {hasEditableLayer && toolPanelOpen && portalTarget && createPortal(
        <>
        <div style={{
          position: "absolute",
          left: isPhone ? "8px" : "16px",
          top: isPhone ? "8px" : "16px",
          width: isPhone ? "min(86vw, 280px)" : "270px",
          maxHeight: isPhone ? "70vh" : "calc(100% - 32px)",
          transform: isPhone && panelCollapsed ? "translateX(calc(-100% - 24px))" : "translateX(0)",
          transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.18s ease",
          opacity: isPhone && panelCollapsed ? 0 : 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          background: "rgba(255,255,255,0.98)",
          borderRadius: "14px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.05)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          pointerEvents: "auto",
          zIndex: 120,
          fontFamily: "Clash Grotesk, sans-serif",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "11px 14px", borderBottom: "1px solid #f0f0f0", flexShrink: 0,
          }}>
            <span style={{
              fontSize: "13px", fontWeight: 700,
              color: eraseActive ? "#7C3AED" : pixelEraseActive ? "#64748b" : drawActive ? "#16a34a" : fillActive ? "#ea580c" : morphActive ? "#0ea5e9" : "#ef4444",
            }}>
              {eraseActive ? "Split" : pixelEraseActive ? "Erase" : drawActive ? "Draw" : fillActive ? "Fill" : morphActive ? "Expand / Shrink" : "Cut"}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {isPhone && <CollapseButton onClick={() => setPanelCollapsed(true)} />}
              <button
                onClick={() => {
                  if (eraseActive) { onEraseCancel?.(); return; }
                  if (smartCutActive) { onSmartCutCancel?.(); return; }
                  runAfterLeaveCut(() => {});
                }}
                style={{
                  background: "none", border: "none", cursor: "pointer", color: "#9ca3af",
                  fontSize: "13px", padding: "4px", borderRadius: "6px", display: "flex", alignItems: "center",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#111"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "12px 14px 14px 14px", overflowY: "auto", flex: 1, minHeight: 0 }}>
            {/* Cut / Erase / Fill / Draw / Expand-Shrink — canvas portals its controls into this slot */}
            {(eraseActive || fillActive || pixelEraseActive || drawActive || morphActive) && (
              <div id="stitch-erase-controls-slot" style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px" }} />
            )}

            {/* Recolor whole layer — color picker + palette + eyedropper, under Fill. */}
            {fillActive && (() => {
              const safeHex = /^#[0-9a-fA-F]{6}$/.test(colors?.[currentLayerIdx] || "")
                ? colors[currentLayerIdx]
                : "#888888";
              const setLayerColor = (hex) => onColorChange?.(currentLayerIdx, hex, colorInteraction.current);
              const palette = Array.from(
                new Set((colors || []).filter((c) => /^#[0-9a-fA-F]{6}$/.test(c)))
              );
              const pickerContent = (
                <div style={{ width: "208px", display: "flex", flexDirection: "column", gap: "10px", fontFamily: "Clash Grotesk, sans-serif" }}>
                  {/* Live color picker — drag to recolor the whole layer. */}
                  <div className="stitch-layer-colorpicker"
                    onPointerDownCapture={() => { colorInteraction.current = Symbol("color drag"); }}
                    onKeyDownCapture={(e) => { if (!e.repeat) colorInteraction.current = Symbol("color key"); }}
                  >
                    <HexColorPicker
                      color={safeHex}
                      onChange={setLayerColor}
                      style={{ width: "100%", height: "132px" }}
                    />
                  </div>

                  {/* Current swatch + hex input. */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{
                      width: "26px", height: "26px", borderRadius: "7px", flexShrink: 0,
                      backgroundColor: safeHex, border: "1px solid rgba(0,0,0,0.18)",
                    }} />
                    <div style={{
                      display: "flex", alignItems: "center", flex: 1,
                      border: "1px solid #e5e7eb", borderRadius: "7px", padding: "0 8px",
                      background: "#fff",
                    }}>
                      <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 600 }}>#</span>
                      <HexColorInput
                        color={safeHex}
                        onChange={setLayerColor}
                        onFocus={() => { colorInteraction.current = Symbol("hex input"); }}
                        onBlur={() => { colorInteraction.current = null; }}
                        style={{
                          flex: 1, width: "100%", border: "none", outline: "none",
                          fontFamily: "Clash Grotesk, sans-serif", fontSize: "12px",
                          fontWeight: 600, color: "#333", textTransform: "uppercase",
                          padding: "6px 4px", background: "transparent",
                        }}
                      />
                    </div>
                  </div>

                  {/* Quick palette pulled from the design's existing layer colors. */}
                  {palette.length > 1 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {palette.map((c) => {
                        const selected = c.toLowerCase() === safeHex.toLowerCase();
                        return (
                          <button
                            key={c}
                            type="button"
                            title={c}
                            onClick={() => onColorChange?.(currentLayerIdx, c)}
                            style={{
                              width: "22px", height: "22px", borderRadius: "6px", cursor: "pointer",
                              backgroundColor: c, padding: 0,
                              border: selected ? "2px solid #0ea5e9" : "1px solid rgba(0,0,0,0.18)",
                              boxShadow: selected ? "0 0 0 2px rgba(14,165,233,0.25)" : "none",
                            }}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Eyedropper — pick any on-screen color. */}
                  {eyedropperSupported && (
                    <button
                      type="button"
                      onClick={pickLayerColor}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "7px", width: "100%",
                        padding: "8px 10px", borderRadius: "9px", cursor: "pointer",
                        border: "1px solid rgba(14,165,233,0.3)", background: "rgba(14,165,233,0.06)",
                        color: "#0284c7", fontSize: "12px", fontWeight: 700,
                        fontFamily: "Clash Grotesk, sans-serif",
                      }}
                    >
                      <EyedropperIcon size={14} /> Eyedropper
                    </button>
                  )}
                </div>
              );
              return (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: "7px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280" }}>
                    Layer color
                  </div>
                  <Popover
                    content={pickerContent}
                    trigger="click"
                    placement="rightTop"
                    overlayInnerStyle={{ padding: "12px", borderRadius: "12px" }}
                  >
                    <button
                      type="button"
                      style={{
                        display: "flex", alignItems: "center", gap: "9px", width: "100%",
                        padding: "8px 10px", borderRadius: "9px", cursor: "pointer",
                        border: "1px solid #e5e7eb", background: "#fff",
                        fontFamily: "Clash Grotesk, sans-serif",
                      }}
                    >
                      <span style={{
                        width: "20px", height: "20px", borderRadius: "6px", flexShrink: 0,
                        backgroundColor: safeHex, border: "1px solid rgba(0,0,0,0.18)",
                      }} />
                      <span style={{ flex: 1, textAlign: "left", fontSize: "12px", fontWeight: 700, color: "#333", textTransform: "uppercase" }}>
                        {safeHex}
                      </span>
                      <RightOutlined style={{ fontSize: "10px", color: "#9ca3af" }} />
                    </button>
                  </Popover>
                </div>
              );
            })()}

            {/* Delete — remove individual shapes */}
            {smartCutActive && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                  <div style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    background: "#ef4444",
                    animation: "pulse-dot 1.2s ease-in-out infinite",
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#ef4444" }}>
                    {isPhone ? "Tap any shape to cut it away" : "Click any shape to cut it away"}
                  </span>
                </div>
                <button
                  onClick={() => onSmartCutCancel?.()}
                  style={{
                    border: "1px solid rgba(0,0,0,0.1)", background: "#fff",
                    color: "#888", fontSize: "11px", fontWeight: 600,
                    padding: "7px 12px", borderRadius: "8px", cursor: "pointer",
                    fontFamily: "Clash Grotesk, sans-serif", width: "100%",
                  }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
        {isPhone && panelCollapsed && (
          <EdgeHandle
            label={eraseActive ? "Split" : pixelEraseActive ? "Erase" : drawActive ? "Draw" : fillActive ? "Fill" : "Cut"}
            accent={eraseActive ? "#7C3AED" : pixelEraseActive ? "#64748b" : drawActive ? "#16a34a" : fillActive ? "#ea580c" : "#ef4444"}
            onClick={() => setPanelCollapsed(false)}
          />
        )}
        </>,
        portalTarget
      )}

      {/* ── Stitch settings — left-docked side panel (portaled to the editor) ── */}
      {hasEditableLayer && stitchMenuOpen && portalTarget && createPortal(
        <>
        <div style={{
          position: "absolute",
          left: isPhone ? "8px" : "16px",
          top: isPhone ? "8px" : "16px",
          bottom: isPhone ? "auto" : "16px",
          maxHeight: isPhone ? "78vh" : undefined,
          width: isPhone ? "min(88vw, 320px)" : "320px",
          transform: isPhone && panelCollapsed ? "translateX(calc(-100% - 24px))" : "translateX(0)",
          transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.18s ease",
          opacity: isPhone && panelCollapsed ? 0 : 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          background: "rgba(255,255,255,0.98)",
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.05)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          pointerEvents: "auto",
          zIndex: 120,
          fontFamily: "Clash Grotesk, sans-serif",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px 12px 16px", borderBottom: "1px solid #f0f0f0", flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <span style={{ width: "13px", height: "13px", borderRadius: "4px", backgroundColor: layerColor, border: "1px solid rgba(0,0,0,0.12)" }} />
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "#111" }}>Stitch</span>
                <span style={{ fontSize: "10px", fontWeight: 500, color: "#9ca3af" }}>{groupTabs.length > 1 ? `Sublayer ${groupTabs.findIndex((t) => t.idx === currentLayerIdx) + 1} of ${groupTabs.length}` : `Layer ${currentLayerIdx + 1}`}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {isPhone && <CollapseButton onClick={() => setPanelCollapsed(true)} />}
              <button
                data-tutorial="stitch-panel-close"
                aria-label="Close stitch settings"
                onClick={() => setStitchMenuOpen(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer", color: "#9ca3af",
                  fontSize: "14px", padding: "4px", borderRadius: "6px", display: "flex", alignItems: "center",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#111"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
          {/* Scroll body */}
          <div className="hide-scrollbar" style={{
            display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px",
            padding: "14px 16px 16px 16px", overflowY: "auto",
            flex: 1, minHeight: 0,
            scrollbarWidth: "none",
          }}>
          <>
          {/* Sublayer switcher — a layer and its sublayers share a color but
              each keeps its own stitch type. Switch between them or add another. */}
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <BarLabel>Sublayers</BarLabel>
            <div style={{ display: "flex", gap: "5px", alignItems: "stretch" }}>
              {groupTabs.length > 1 && (
                <div className="hide-scrollbar" style={{
                  display: "flex", gap: "2px", padding: "2px", background: "#f4f1fa",
                  borderRadius: "9px", flex: 1, overflowX: "auto", scrollbarWidth: "none",
                }}>
                  {groupTabs.map((m) => {
                    const selected = m.idx === currentLayerIdx;
                    return (
                      <button
                        key={m.idx}
                        onClick={() => onLayerChange?.(m.idx)}
                        style={{
                          flex: "1 0 auto", minWidth: "42px", padding: "5px 8px",
                          border: "none", borderRadius: "7px",
                          background: selected ? "#fff" : "transparent",
                          boxShadow: selected ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                          color: selected ? "#7C3AED" : "#888", fontSize: "11px", fontWeight: 700,
                          cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif", whiteSpace: "nowrap",
                        }}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              )}
              <Tooltip title="Add a sublayer (same color, its own stitch type)" placement="bottom" color="#333" overlayInnerStyle={TOOLTIP_STYLE} mouseEnterDelay={0.4}>
                <button
                  onClick={() => onAddSublayer?.(currentLayerIdx)}
                  style={{
                    flex: groupTabs.length > 1 ? "0 0 auto" : "1 1 auto",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "4px",
                    padding: "6px 12px", border: "1px dashed rgba(124,58,237,0.45)",
                    borderRadius: "8px", background: "rgba(124,58,237,0.05)",
                    color: "#7C3AED", fontSize: "11px", fontWeight: 700, cursor: "pointer",
                    fontFamily: "Clash Grotesk, sans-serif", whiteSpace: "nowrap",
                  }}
                >
                  + Sublayer
                </button>
              </Tooltip>
            </div>
          </div>
          {/* Stitch type selector */}
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <BarLabel>Type</BarLabel>
            <div style={{ display: "flex", gap: "2px", padding: "2px", background: "#f4f1fa", borderRadius: "9px" }}>
              {STITCH_TYPES.map((t) => {
                const selected = currentStitchFamily === t.value;
                const aiRec = aiRecType === t.value && !selected;
                return (
                  <Tooltip key={t.value} title={t.hint} placement="bottom" color="#333" overlayInnerStyle={TOOLTIP_STYLE} mouseEnterDelay={0.4}>
                    <button
                      data-tutorial={`stitch-type-${t.value}`}
                      onClick={() => chooseStitchType(t.value)}
                      style={{
                        position: "relative",
                        flex: 1,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
                        height: "28px", padding: "0 8px",
                        border: "none", borderRadius: "7px", cursor: "pointer",
                        background: selected ? "#fff" : "transparent",
                        boxShadow: selected ? "0 1px 3px rgba(36,21,68,0.14)" : "none",
                        color: selected ? "#7C3AED" : "#6b6480",
                        fontWeight: selected ? 700 : 600, fontSize: "11px",
                        fontFamily: "Clash Grotesk, sans-serif", transition: "all 0.14s ease",
                      }}
                    >
                      <span style={{ display: "flex", lineHeight: 1 }}><t.Icon size={14} /></span>
                      {t.label}
                      {aiRec && (
                        <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "linear-gradient(135deg,#F453C4,#7C3AED)" }} />
                      )}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Plain-language description of the selected stitch type */}
          <div style={{
            fontSize: "11.5px", lineHeight: 1.45, color: "#6b7280",
            background: "#f8f7fc", border: "1px solid #efeaf7",
            borderRadius: "8px", padding: "8px 10px",
            fontFamily: "Clash Grotesk, sans-serif",
          }}>
            {isSatin
              ? "Smooth, glossy stitches that zig-zag between two edges. Best for borders, outlines and lettering."
              : isRunning
              ? "A thin single line of stitches that follows the shape. Best for outlines and fine details."
              : "Solid rows of stitching that fill in a whole area. Best for large shapes and backgrounds."}
          </div>

          {/* Fill: method + angle */}
          {isFill && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <BarLabel>Pattern</BarLabel>
                <Select
                  value={stitchSettings.fill_method || "auto_fill"}
                  onChange={(v) => {
                    embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, { fill_method: v, stitchType: v });
                    setStitchSettings((p) => ({ ...p, fill_method: v, stitch_type: v }));
                  }}
                  size="small"
                  style={{ width: "100%", fontFamily: "Clash Grotesk, sans-serif" }}
                  dropdownStyle={{ fontFamily: "Clash Grotesk, sans-serif" }}
                  options={FILL_METHODS}
                />
                <span style={{ fontSize: "10.5px", color: "#9ca3af", lineHeight: 1.4 }}>
                  How the rows are laid out inside the shape (Auto Fill suits most designs).
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <BarLabel>Angle</BarLabel>
                <div style={{ display: "flex", gap: "2px", flexWrap: "wrap", alignItems: "center" }}>
                  {ANGLE_PRESETS.map((a) => (
                    <AngleChip
                      key={a.label}
                      label={a.label}
                      isActive={stitchSettings.angle === a.value}
                      onClick={() => {
                        embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, { angle: a.value });
                        setStitchSettings((p) => ({ ...p, angle: a.value }));
                      }}
                    />
                  ))}
                  {stitchSettings.angle != null && !ANGLE_PRESETS.some((a) => a.value === stitchSettings.angle) && (
                    <span style={{
                      padding: "3px 7px", fontSize: "10px", fontWeight: 600,
                      background: "#f3e8ff", color: "#7C3AED", borderRadius: "5px",
                      fontFamily: "Clash Grotesk, sans-serif",
                    }}>
                      {stitchSettings.angle}°
                    </span>
                  )}
                </div>
                <span style={{ fontSize: "10.5px", color: "#9ca3af", lineHeight: 1.4 }}>
                  Direction the stitches run. Auto picks the best angle for the shape.
                </span>
              </div>
            </>
          )}

          {/* Satin: edge controls */}
          {isSatin && (
          <>
            <span
              style={{
                color: "#6b7280",
                fontSize: "10px",
                fontWeight: 700,
                fontFamily: "Clash Grotesk, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              }}
            >
              Create Satin Columns
            </span>
            {activeRailDrawing ? (
              <>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                <div style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: activeRailDrawing.railIndex === 0 ? "#7c3aed" : "#db2777",
                  animation: "pulse-dot 1.2s ease-in-out infinite",
                }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#555" }}>
                  Satin Edge {activeRailDrawing.railIndex + 1}
                </span>
                <span style={{ fontSize: "10px", color: "#999", fontWeight: 500 }}>
                  {activeRailDrawing.points.length} pts · Click for points · drag for curves
                </span>
                <button
                  onClick={onFinishRail}
                  style={{
                    border: "none", background: "linear-gradient(135deg, #F453C4, #7C3AED)",
                    color: "#fff",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 16px" : "4px 12px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  Finish
                </button>
                <button
                  onClick={onCancelRail}
                  style={{
                    border: "1px solid #fca5a5", background: "#fef2f2",
                    color: "#dc2626",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 14px" : "4px 10px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  Cancel
                </button>
              </div>
              <SatinTutorialSteps method="draw" />
              </>
            ) : railEditing ? (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                <span style={{ width: "100%", fontSize: "11px", color: "#6b7280" }}>Drag a box to select points, then press Delete. Shift adds to selection. Space + drag pans.</span>
                <div style={{
                  width: "7px", height: "7px", borderRadius: "50%",
                  background: getSatinColumnColor(railEditing.pairIdx).stroke,
                  animation: "pulse-dot 1.2s ease-in-out infinite",
                }} />
                <span style={{ fontSize: "11px", fontWeight: 700, color: getSatinColumnColor(railEditing.pairIdx).text }}>
                  Editing Column {railEditing.pairIdx + 1}, Edge {railEditing.railIndex + 1}
                </span>
                <span style={{ fontSize: "10px", color: "#6b7280" }}>
                  Drag nodes · click small dots to add · Shift-drag to constrain
                </span>
                {[0, 1].map((railIndex) => (
                  <button
                    key={`active-edit-edge-${railIndex}`}
                    onClick={() => onStartRailEditing?.(currentLayerIdx, railEditing.pairIdx, railIndex)}
                    style={{
                      border: `1px solid ${getSatinColumnColor(railEditing.pairIdx).border}`,
                      background: railEditing.railIndex === railIndex ? getSatinColumnColor(railEditing.pairIdx).stroke : "#fff",
                      color: railEditing.railIndex === railIndex ? "#fff" : getSatinColumnColor(railEditing.pairIdx).text,
                      fontSize: "10px", fontWeight: 700, padding: "5px 8px", borderRadius: "6px",
                      cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                    }}
                  >
                    Edge {railIndex + 1}
                  </button>
                ))}
                <button
                  onClick={onCurveRailEdge}
                  title="Convert this edge to a smooth Bézier curve"
                  style={{ border: "1px solid #c4b5fd", background: "#f5f3ff", color: "#6d28d9", fontSize: "10px", fontWeight: 700, padding: "5px 9px", borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif" }}
                >
                  Curve edge
                </button>
                <button
                  onClick={() => onSetRailNodeType?.("smooth")}
                  title="Make the selected node smooth with linked handles"
                  style={{ border: "1px solid #d1d5db", background: "#fff", color: "#4b5563", fontSize: "10px", fontWeight: 700, padding: "5px 9px", borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif" }}
                >
                  Smooth node
                </button>
                <button
                  onClick={() => onSetRailNodeType?.("corner")}
                  title="Remove the selected node's control handles"
                  style={{ border: "1px solid #d1d5db", background: "#fff", color: "#4b5563", fontSize: "10px", fontWeight: 700, padding: "5px 9px", borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif" }}
                >
                  Corner
                </button>
                <button
                  onClick={onDeleteRailNode}
                  style={{ border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", fontSize: "10px", fontWeight: 700, padding: "5px 9px", borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif" }}
                >
                  Delete points{railEditing.selectedNodes?.length ? ` (${railEditing.selectedNodes.length})` : ""}
                </button>
                <button
                  onClick={onReverseRailColumn}
                  style={{ border: "1px solid #d1d5db", background: "#fff", color: "#4b5563", fontSize: "10px", fontWeight: 700, padding: "5px 9px", borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif" }}
                >
                  Reverse column
                </button>
                <button
                  onClick={onFinishRailEditing}
                  style={{ border: "none", background: "linear-gradient(135deg, #22c55e, #16a34a)", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "5px 12px", borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif" }}
                >
                  Done
                </button>
              </div>
            ) : railSelection ? (
              <>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                <div style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: railSelection.selectingRail === 0 ? "#3b82f6" : "#ec4899",
                  animation: "pulse-dot 1.2s ease-in-out infinite",
                }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#555" }}>
                  Click the path to use as Satin Edge {railSelection.selectingRail + 1}
                </span>
                {railSelection.rail0PathIdx != null && (
                  <span style={{
                    fontSize: "9px", fontWeight: 600, color: "#3b82f6",
                    padding: "2px 6px", borderRadius: "4px", background: "rgba(59,130,246,0.08)",
                  }}>
                    Edge 1 set
                  </span>
                )}
                <button
                  data-tutorial="satin-rail-selection-done"
                  onClick={onCancelRailSelection}
                  style={{
                    border: "none", background: "linear-gradient(135deg, #22c55e, #16a34a)",
                    color: "#fff",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 16px" : "4px 12px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  Done
                </button>
              </div>
              <SatinTutorialSteps method="select" />
              </>
            ) : activeRungDrawing ? (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: activeRungColor.stroke,
                  animation: "pulse-dot 1.2s ease-in-out infinite",
                }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: activeRungColor.text }}>
                  Column {(activeRungDrawing.pairIdx || 0) + 1}: {activeRungDrawing.startPoint ? "click where the guide ends on Edge 2" : "click where the guide starts on Edge 1"}
                </span>
                <button
                  data-tutorial="satin-guide-drawing-done"
                  onClick={onFinishRungDrawing}
                  style={{
                    border: "none", background: "linear-gradient(135deg, #22c55e, #16a34a)",
                    color: "#fff",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 16px" : "4px 12px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  Done
                </button>
                <button
                  onClick={onFinishRungDrawing}
                  style={{
                    border: "1px solid #fca5a5", background: "#fef2f2",
                    color: "#dc2626",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 14px" : "4px 10px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : embroidery?.cutMode ? (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: "#8b5cf6",
                  animation: "pulse-dot 1.2s ease-in-out infinite",
                }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#555" }}>
                  {embroidery.cutMode.point1 ? "Click the opposite edge to slice through" : "Click one edge of the outline to start"}
                </span>
                {embroidery.cutMode.pieces.length > 1 && (
                  <span style={{
                    fontSize: "9px", fontWeight: 600, color: "#8b5cf6",
                    padding: "2px 6px", borderRadius: "4px", background: "rgba(139,92,246,0.1)",
                  }}>
                    {embroidery.cutMode.pieces.length} pieces
                  </span>
                )}
                {embroidery.cutMode.pieces.length > 1 && (
                  <button
                    onClick={() => embroidery.undoLastCut()}
                    style={{
                      border: "1px solid rgba(0,0,0,0.1)", background: "#fff",
                      color: "#888", fontSize: "10px", fontWeight: 600, padding: "4px 8px",
                      borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                    }}
                  >
                    Undo
                  </button>
                )}
                <button
                  onClick={() => {
                    const result = embroidery.finishCutMode();
                    if (result && result.pieces.length > 1 && onCutFinished) {
                      onCutFinished(result.layerIndex, result.pieces);
                    } else {
                      embroidery.cancelCutMode();
                    }
                  }}
                  style={{
                    background: embroidery.cutMode.pieces.length > 1 ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : "#fff",
                    color: embroidery.cutMode.pieces.length > 1 ? "#fff" : "#888",
                    border: embroidery.cutMode.pieces.length > 1 ? "none" : "1px solid rgba(0,0,0,0.1)",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 16px" : "4px 12px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  {embroidery.cutMode.pieces.length > 1 ? "Done" : "Cancel"}
                </button>
              </div>
            ) : embroidery?.splitMode ? (
              <>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                <div style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: "#f59e0b",
                  animation: "pulse-dot 1.2s ease-in-out infinite",
                }} />
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#555" }}>
                  {embroidery.splitMode.point1 ? "Click the opposite edge to split into two" : "Click one edge of the outline to start"}
                </span>
                {railPairCount > 0 && (
                  <button
                    onClick={() => onUndoLastSplitPair(currentLayerIdx)}
                    style={{
                      background: "#fff",
                      color: "#d97706",
                      border: "1px solid rgba(245,158,11,0.3)",
                      fontSize: "10px", fontWeight: 600, padding: "4px 10px",
                      borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                    }}
                  >
                    Undo
                  </button>
                )}
                <button
                  data-tutorial="satin-split-done"
                  onClick={() => embroidery.cancelSplitMode()}
                  style={{
                    background: railPairCount > 0 ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : "#fff",
                    color: railPairCount > 0 ? "#fff" : "#888",
                    border: railPairCount > 0 ? "none" : "1px solid rgba(0,0,0,0.1)",
                    fontSize: isPhone ? "12px" : "10px",
                    fontWeight: 700,
                    padding: isPhone ? "8px 16px" : "4px 12px",
                    minHeight: isPhone ? "36px" : undefined,
                    borderRadius: "6px", cursor: "pointer", fontFamily: "Clash Grotesk, sans-serif",
                  }}
                >
                  {railPairCount > 0 ? "Done" : "Cancel"}
                </button>
              </div>
              <SatinTutorialSteps method="split" />
              </>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <AutoSatinBeta mmPerUnit={autoSatinMmPerUnit} jobRevision={autoSatinJobRevision} jobContext={autoSatinJobContext} key={currentLayerIdx} pathD={autoSatinPath} revision={autoSatinRevision} retryRequest={satinRetryRequest} onRetryRequestHandled={onSatinRetryRequestHandled} onPreview={onAutoSatinPreview} onApply={onAutoSatinApply} onDiscard={onAutoSatinDiscard} />
                  <div
                    aria-label="Manual satin editing"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                      color: "#7b7480",
                      fontSize: "11px",
                      fontWeight: 600,
                    }}
                  >
                    <span aria-hidden="true" style={{ flex: 1, height: "1px", background: "#e7e3e9" }} />
                    <span>Or start manually editing</span>
                    <span aria-hidden="true" style={{ flex: 1, height: "1px", background: "#e7e3e9" }} />
                  </div>
                  {(() => {
                    const pieces = layerPieces?.[currentLayerIdx];
                    if (pieces && pieces.length > 1) {
                      return pieces.map((piece, pi) => {
                        const startSplit = () => {
                          if (embroidery?.startSplitMode) {
                            embroidery.startSplitMode(currentLayerIdx, piece.d, onSplitRailsCreated);
                          }
                        };
                        return (
                          <SatinMethodButton
                        compact={hasRails}
                            key={`split-piece-${pi}`}
                            accent="#d97706" bg="rgba(217,119,6,0.07)" border="rgba(217,119,6,0.25)"
                            title={`Split into Satin Columns ${pi + 1}`}
                            desc="Click two opposite edges of this shape — satin fills between them."
                            icon={SATIN_METHOD_ICONS.split}
                            onClick={startSplit}
                          />
                        );
                      });
                    }
                    const startSplit = () => {
                      const pathD = svgJoined?.children?.[currentLayerIdx]?.attributes?.d;
                      if (pathD && embroidery?.startSplitMode) {
                        embroidery.startSplitMode(currentLayerIdx, pathD, onSplitRailsCreated);
                      }
                    };
                    return (
                      <SatinMethodButton
                        compact={hasRails}
                        accent="#d97706" bg="rgba(217,119,6,0.07)" border="rgba(217,119,6,0.25)"
                        title="Split into Satin Columns"
                        desc="Click two opposite edges of one shape — satin fills between them."
                        icon={SATIN_METHOD_ICONS.split}
                        onClick={startSplit}
                      />
                    );
                  })()}
                  <SatinMethodButton
                        compact={hasRails}
                    accent="#7C3AED" bg="rgba(124,58,237,0.07)" border="rgba(124,58,237,0.25)"
                    title="Select Satin Columns"
                    desc="Pick two existing paths to use as the left and right edges."
                    icon={SATIN_METHOD_ICONS.select}
                    onClick={() => onStartRailSelection(currentLayerIdx)}
                  />
                  <SatinMethodButton
                        compact={hasRails}
                    accent="#3b82f6" bg="rgba(59,130,246,0.07)" border="rgba(59,130,246,0.28)"
                    title="Draw Edges"
                    desc="Draw the two side edges yourself with the pen tool."
                    icon={SATIN_METHOD_ICONS.draw}
                    onClick={() => onStartRailDrawing?.(currentLayerIdx, 0)}
                  />
                  {hasRails && (
                    <SatinColumnControls
                      pairs={currentRailPairs}
                      selectedPairIdx={selectedColumnIdx}
                      onSelect={selectColumn}
                      onAddGuide={(pairIdx) => { selectColumn(pairIdx); onStartRungDrawing?.(currentLayerIdx, pairIdx); }}
                      onEditEdge={(pairIdx, edge) => { selectColumn(pairIdx); onStartRailEditing?.(currentLayerIdx, pairIdx, edge); }}
                      onClearGuides={(pairIdx) => onClearRungs?.(currentLayerIdx, pairIdx)}
                      onClearAll={() => onClearRails(currentLayerIdx)}
                    />
                  )}
              </div>
            )}
          </>
          )}
          </>


          {/* Primary satin adjustments; specialist settings stay collapsed. */}
          {isSatin && (
            <div style={{ marginTop: "2px", paddingTop: "12px", borderTop: "1px solid #f0f0f0" }}>
              <SliderRow
                label="Pull compensation"
                hint="Pull compensation: adds width on both sides of the satin column. This can help cover gaps beside the fill without changing the fill shape."
                value={stitchSettings.pull_compensation_mm ?? SATIN_PULL_COMP_MM}
                onChange={(value) => {
                  const patch = { pull_compensation_mm: value };
                  setStitchSettings((prev) => ({ ...prev, ...patch }));
                  embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, patch);
                }}
                min={0} max={1.0} step={0.05} unit="mm"
              />
              <p style={{ fontSize: "11px", color: "#6b7280", lineHeight: 1.4, margin: "0 0 12px" }}>
                Widens satin edges to help close gaps between the satin and fill.
              </p>
              <SliderRow
                label="Zigzag spacing"
                hint="Distance between zigzag stitches along the column. Smaller spacing makes the satin denser."
                value={stitchSettings.zigzag_spacing_mm ?? 0.4}
                onChange={(value) => {
                  const patch = { zigzag_spacing_mm: value };
                  setStitchSettings((prev) => ({ ...prev, ...patch }));
                  embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, patch);
                }}
                min={0.2} max={1.5} step={0.05} unit="mm"
              />
              <details>
              <summary style={{ cursor: "pointer", color: "#6b7280", fontSize: "11px", fontWeight: 600, padding: "4px 0 12px" }}>
                Advanced settings
              </summary>
              <SatinAdvancedSettings
                stitchSettings={stitchSettings}
                setStitchSettings={setStitchSettings}
                embroidery={embroidery}
                currentLayerIdx={currentLayerIdx}
                activePassIndex={activePassIndex}
              />
              <button
                onClick={() => {
                  setStitchSettings((prev) => ({ ...prev, ...SATIN_DEFAULTS }));
                  embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, SATIN_DEFAULTS);
                }}
                style={{
                  marginTop: "14px", width: "100%", background: "none",
                  border: "1px solid #e5e7eb", borderRadius: "8px",
                  padding: "8px 16px", cursor: "pointer", fontSize: "12px",
                  fontWeight: 600, color: "#6b7280", fontFamily: "Clash Grotesk, sans-serif",
                }}
              >
                Reset to defaults
              </button>
              </details>
            </div>
          )}

          {/* Advanced fill / running settings — fully editable inline */}
          {(isFill || isRunning) && (
            <div style={{ marginTop: "2px", paddingTop: "14px", borderTop: "1px solid #f0f0f0" }}>
              <AutoFillAdvancedSettings
                stitchSettings={stitchSettings}
                setStitchSettings={setStitchSettings}
                embroidery={embroidery}
                currentLayerIdx={currentLayerIdx}
                activePassIndex={activePassIndex}
                isRunning={isRunning}
                singleColumn
              />
              <button
                onClick={() => {
                  const defaults = isRunning ? RUNNING_DEFAULTS : FILL_DEFAULTS;
                  setStitchSettings((prev) => ({ ...prev, ...defaults }));
                  embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, defaults);
                }}
                style={{
                  marginTop: "14px", width: "100%", background: "none",
                  border: "1px solid #e5e7eb", borderRadius: "8px",
                  padding: "8px 16px", cursor: "pointer", fontSize: "12px",
                  fontWeight: 600, color: "#6b7280", fontFamily: "Clash Grotesk, sans-serif",
                }}
              >
                Reset to defaults
              </button>
            </div>
          )}
          </div>{/* scroll body */}
        </div>
        {isPhone && panelCollapsed && (
          <EdgeHandle
            label="Stitch"
            accent="#7C3AED"
            swatch={layerColor}
            onClick={() => setPanelCollapsed(false)}
          />
        )}
        </>,
        portalTarget
      )}

      {/* "More settings" modal — same window-style dialog used by Export. */}
      <Modal
        open={moreOpen}
        onCancel={() => setMoreOpen(false)}
        width={680}
        centered
        closable={false}
        footer={null}
        styles={{ body: { padding: 0 }, content: { borderRadius: 16, overflow: "hidden", padding: 0 } }}
      >
        {/* Header — mirrors the Export modal */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 22px 14px 22px",
          borderBottom: "1px solid #f0f0f0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "14px", height: "14px", borderRadius: "4px",
              backgroundColor: layerColor,
              border: "1px solid rgba(0,0,0,0.1)", flexShrink: 0,
            }} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
              <span style={{ fontSize: "16px", fontWeight: 700, fontFamily: "Clash Grotesk", color: "#111" }}>
                {moreTitle}
              </span>
              <span style={{ fontSize: "11px", fontWeight: 500, color: "#9ca3af", fontFamily: "Clash Grotesk" }}>
                {moreSubtitle}
              </span>
            </div>
          </div>
          <button
            onClick={() => setMoreOpen(false)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#9ca3af", fontSize: "16px", padding: "4px",
              borderRadius: "6px", display: "flex", alignItems: "center",
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#111"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
          >
            <CloseOutlined />
          </button>
        </div>
        {/* Scrollable body */}
        <div style={{ padding: "20px 24px", maxHeight: "65vh", overflowY: "auto" }}>
          <AutoFillAdvancedSettings
            stitchSettings={stitchSettings}
            setStitchSettings={setStitchSettings}
            embroidery={embroidery}
            currentLayerIdx={currentLayerIdx}
            activePassIndex={activePassIndex}
            isRunning={isRunning}
          />
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 24px 16px 24px",
          borderTop: "1px solid #f0f0f0",
        }}>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => {
                const defaults = isRunning ? RUNNING_DEFAULTS : FILL_DEFAULTS;
                setStitchSettings((prev) => ({ ...prev, ...defaults }));
                embroidery?.updatePassSettings?.(currentLayerIdx, activePassIndex, defaults);
              }}
              style={{
                background: "none", border: "1px solid #e5e7eb", borderRadius: "8px",
                padding: "7px 16px", cursor: "pointer", fontSize: "12px",
                fontWeight: 600, color: "#6b7280", fontFamily: "Clash Grotesk, sans-serif",
                transition: "border-color 0.15s, color 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#374151"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#6b7280"; }}
            >
              Reset to defaults
            </button>
          </div>
          <button
            onClick={() => setMoreOpen(false)}
            style={{
              background: "linear-gradient(135deg,#F453C4 0%,#c7429f 100%)",
              border: "none", borderRadius: "8px",
              padding: "7px 24px", cursor: "pointer", fontSize: "12px",
              fontWeight: 700, color: "#fff", fontFamily: "Clash Grotesk, sans-serif",
              boxShadow: "0 3px 12px rgba(244,83,196,0.4)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 5px 16px rgba(244,83,196,0.5)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 3px 12px rgba(244,83,196,0.4)"; }}
          >
            Done
          </button>
        </div>
      </Modal>

      {/* Confirm any stitch-type change: keep the current stitching on this
          layer and add the new type on a sublayer, or replace this layer. */}
      <Modal
        open={!!switchPrompt}
        onCancel={() => setSwitchPrompt(null)}
        footer={null}
        centered
        width={440}
        title={null}
        closable={false}
        bodyStyle={{ padding: "22px" }}
      >
        {(() => {
          if (!switchPrompt) return null;
          const fromLabel = typeLabel(switchPrompt.from);
          const toLabel = typeLabel(switchPrompt.value);
          const losesEdges = switchPrompt.from === "satin" && hasRails;
          const ff = "Clash Grotesk, sans-serif";
          const cardBase = {
            width: "100%", textAlign: "left", display: "flex", gap: "12px",
            alignItems: "flex-start", padding: "13px 14px", borderRadius: "12px",
            cursor: "pointer", fontFamily: ff, transition: "all 0.13s ease",
            background: "#fff",
          };
          return (
            <>
              <div style={{ fontSize: "17px", fontWeight: 700, color: "#1f2937", fontFamily: ff, marginBottom: "5px" }}>
                Switch from {fromLabel} to {toLabel}?
              </div>
              <div style={{ fontSize: "13px", color: "#6b7280", lineHeight: 1.5, marginBottom: "18px", fontFamily: ff }}>
                Keep this layer as <strong>{fromLabel}</strong> and add{" "}
                <strong>{toLabel}</strong> on a new sublayer, or replace this layer&rsquo;s stitch type.
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {/* Replace this layer */}
                <button
                  data-tutorial="stitch-switch-replace-layer"
                  onClick={() => { applyStitchType(switchPrompt.value); setSwitchPrompt(null); }}
                  style={{ ...cardBase, border: "1.5px solid #e5e7eb" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.borderColor = "#9ca3af"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
                >
                  <span style={{
                    flexShrink: 0, width: "34px", height: "34px", borderRadius: "9px",
                    background: "#f3f4f6",
                    color: "#4b5563", display: "flex",
                    alignItems: "center", justifyContent: "center", fontSize: "17px",
                  }}>↺</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1f2937" }}>
                      Replace this layer
                    </span>
                    <span style={{ display: "block", fontSize: "12px", color: "#6b7280", marginTop: "3px", lineHeight: 1.45 }}>
                      {losesEdges
                        ? `Changes this layer to ${toLabel} and deletes its satin edges.`
                        : `Changes this layer’s stitch type to ${toLabel}.`}
                    </span>
                  </span>
                </button>

                {/* Keep both via a sublayer */}
                <button
                  onClick={() => addSublayerWithType(switchPrompt.value)}
                  style={{ ...cardBase, border: "1.5px solid #e5e7eb" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.borderColor = "#9ca3af"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
                >
                  <span style={{
                    flexShrink: 0, width: "34px", height: "34px", borderRadius: "9px",
                    background: "#f3f4f6", color: "#4b5563", display: "flex",
                    alignItems: "center", justifyContent: "center", fontSize: "17px",
                  }}>⧉</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                      <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1f2937" }}>Keep both</span>
                    </span>
                    <span style={{ display: "block", fontSize: "12px", color: "#6b7280", marginTop: "3px", lineHeight: 1.45 }}>
                      Keeps {fromLabel} here and stitches {toLabel} on a new sublayer.
                    </span>
                  </span>
                </button>

                <button
                  onClick={() => setSwitchPrompt(null)}
                  style={{
                    width: "100%", padding: "9px 14px", border: "none", background: "none",
                    color: "#9ca3af", fontSize: "12.5px", fontWeight: 600, marginTop: "2px",
                    cursor: "pointer", fontFamily: ff,
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

export default StitchToolbar;
